import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, Square, Clock, Activity, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MonitoringSessionManager = () => {
  const [formData, setFormData] = useState({
    batch_id: '',
    product_name: '',
    product_type: '',
    species_or_category: '',
    supplier: '',
    monitoring_session_name: '',
    sensor_id: 'DS18B20-01',
  });

  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);

  useEffect(() => {
    fetchActiveSession();
    fetchSessionHistory();
  }, []);

  const fetchActiveSession = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/monitoring-sessions/active`);
      if (response.data && response.data.length > 0) {
        setActiveSession(response.data[0]);
      } else {
        setActiveSession(null);
      }
    } catch (error) {
      console.error('Error fetching active session:', error);
    }
  };

  const fetchSessionHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/monitoring-sessions/history`);
      setSessionHistory(response.data.slice(0, 10));
    } catch (error) {
      console.error('Error fetching session history:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStartMonitoring = async (e) => {
    e.preventDefault();

    if (!formData.batch_id || !formData.product_name || !formData.monitoring_session_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/monitoring-sessions`, formData);
      
      setActiveSession(response.data);
      toast.success(`Monitoring session started for ${formData.product_name}`);
      
      // Clear form
      setFormData({
        batch_id: '',
        product_name: '',
        product_type: '',
        species_or_category: '',
        supplier: '',
        monitoring_session_name: '',
        sensor_id: 'DS18B20-01',
      });

      fetchSessionHistory();
    } catch (error) {
      console.error('Error starting monitoring session:', error);
      toast.error('Failed to start monitoring session');
    } finally {
      setLoading(false);
    }
  };

  const handleEndSession = async () => {
    if (!activeSession) return;

    try {
      await axios.post(`${BACKEND_URL}/api/monitoring-sessions/${activeSession.id}/end`);
      toast.success('Monitoring session ended');
      setActiveSession(null);
      fetchSessionHistory();
    } catch (error) {
      console.error('Error ending session:', error);
      toast.error('Failed to end monitoring session');
    }
  };

  const getSessionDuration = (startTime) => {
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h`;
    if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m`;
    return `${diffMins}m`;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Active Session Display */}
      {activeSession && (
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl p-4 sm:p-6 border-2 border-emerald-300">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div className="flex items-start gap-3 min-w-0">
              <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 animate-pulse flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-emerald-900" style={{ fontFamily: 'Manrope' }}>
                  Active Monitoring Session
                </h3>
                <p className="text-xs sm:text-sm text-emerald-700 break-words">{activeSession.monitoring_session_name}</p>
              </div>
            </div>
            <Button
              onClick={handleEndSession}
              data-testid="end-session-button"
              className="bg-rose-600 hover:bg-rose-700 text-white h-11 self-start sm:self-auto flex-shrink-0"
            >
              <Square className="w-4 h-4 mr-2" />
              End Session
            </Button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
            <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-emerald-200 min-w-0">
              <p className="text-[10px] sm:text-xs text-slate-600 mb-1">Product</p>
              <p className="text-sm sm:text-base font-semibold text-slate-900 truncate">{activeSession.product_name}</p>
              <p className="text-[10px] sm:text-xs text-slate-500 truncate">{activeSession.species_or_category}</p>
            </div>
            <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-emerald-200 min-w-0">
              <p className="text-[10px] sm:text-xs text-slate-600 mb-1">Batch ID</p>
              <p className="text-sm sm:text-base font-semibold text-slate-900 truncate">{activeSession.batch_id}</p>
            </div>
            <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-emerald-200 min-w-0">
              <p className="text-[10px] sm:text-xs text-slate-600 mb-1">Sensor ID</p>
              <p className="text-sm sm:text-base font-semibold text-slate-900 truncate">{activeSession.sensor_id}</p>
            </div>
            <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-emerald-200 min-w-0">
              <p className="text-[10px] sm:text-xs text-slate-600 mb-1">Duration</p>
              <p className="text-sm sm:text-base font-semibold text-emerald-600">
                <Clock className="w-3.5 h-3.5 inline mr-1" />
                {getSessionDuration(activeSession.start_time)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create New Session Form */}
      {!activeSession && (
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200">
          <h3 className="text-base sm:text-lg lg:text-xl font-bold text-slate-800 mb-3 sm:mb-4" style={{ fontFamily: 'Manrope' }}>
            Create Monitoring Session
          </h3>

          <form onSubmit={handleStartMonitoring} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                  Batch ID <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text" name="batch_id" value={formData.batch_id} onChange={handleInputChange}
                  placeholder="e.g., SALMON-2026-001" required
                  className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                  Product Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text" name="product_name" value={formData.product_name} onChange={handleInputChange}
                  placeholder="e.g., Premium Atlantic Salmon" required
                  className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                  Product Type <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text" name="product_type" value={formData.product_type} onChange={handleInputChange}
                  placeholder="e.g., Fresh Seafood" required
                  className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                  Species / Category <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text" name="species_or_category" value={formData.species_or_category} onChange={handleInputChange}
                  placeholder="e.g., Atlantic Salmon" required
                  className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                  Supplier (Optional)
                </label>
                <input
                  type="text" name="supplier" value={formData.supplier} onChange={handleInputChange}
                  placeholder="e.g., Norwegian Seafood AS"
                  className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                  Monitoring Session Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text" name="monitoring_session_name" value={formData.monitoring_session_name} onChange={handleInputChange}
                  placeholder="e.g., SALMON-SHIPMENT-2026-W22" required
                  className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                  Sensor ID
                </label>
                <input
                  type="text" name="sensor_id" value={formData.sensor_id} onChange={handleInputChange}
                  className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="start-monitoring-button"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base font-semibold"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Starting...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Play className="w-5 h-5" />
                  Start Monitoring
                </span>
              )}
            </Button>
          </form>
        </div>
      )}

      {/* Session History */}
      {sessionHistory.length > 0 && (
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200">
          <h3 className="text-base sm:text-lg lg:text-xl font-bold text-slate-800 mb-3 sm:mb-4" style={{ fontFamily: 'Manrope' }}>
            Recent Sessions
          </h3>
          <div className="space-y-2">
            {sessionHistory.slice(0, 5).map((session) => (
              <div
                key={session.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm sm:text-base font-semibold text-slate-900 truncate">{session.product_name}</p>
                  <p className="text-[11px] sm:text-sm text-slate-600 break-words">
                    {session.batch_id} • {session.monitoring_session_name}
                  </p>
                </div>
                <div className="flex items-center sm:flex-col sm:items-end gap-2 sm:gap-0 flex-shrink-0">
                  {session.status === 'active' ? (
                    <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-0.5 sm:py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap">
                      <Activity className="w-3 h-3" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-0.5 sm:py-1 bg-slate-200 text-slate-700 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap">
                      <CheckCircle className="w-3 h-3" />
                      Ended
                    </span>
                  )}
                  <p className="text-[10px] sm:text-xs text-slate-500 sm:mt-1">
                    {new Date(session.start_time).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoringSessionManager;
